import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Ear, Loader2, History, PenLine, Users, Inbox, Check, X, CalendarClock, Clock, Pencil, Trash2, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GroupMultiSelect from "@/components/GroupMultiSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useReference } from "@/lib/useReference";

const ALL = "__all__";

function formatSentAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { timeZone: "America/Chicago",
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// Convert an ISO/UTC string into a value suitable for <input type="datetime-local">
function toLocalInput(iso) {
  const d = iso ? new Date(iso) : new Date(Date.now() + 60 * 60 * 1000);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Minimum selectable time = one minute from now (in local input format)
function nowLocalInput() {
  return toLocalInput(new Date(Date.now() + 60 * 1000).toISOString());
}

export function BroadcastDialog({ variant = "coach" }) {
  const isAdmin = variant === "admin";
  const { ref } = useReference();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("compose");
  const [form, setForm] = useState({ title: "", message: "", district_id: "", campus_id: "", team_id: "", recipient_role: "team_member" });
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [teamTotal, setTeamTotal] = useState(null);
  const [selSubs, setSelSubs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [reqCount, setReqCount] = useState(0);
  const [reqLoading, setReqLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null); // draft id
  const [reviewForm, setReviewForm] = useState({ title: "", message: "", subgroup_ids: [] });
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actLoading, setActLoading] = useState(false);
  const [sendMode, setSendMode] = useState("now"); // "now" | "later"
  const [celebrate, setCelebrate] = useState(false); // coach: mark as a congrats Rally Call
  const [scheduledAt, setScheduledAt] = useState(nowLocalInput());
  const [scheduled, setScheduled] = useState([]);
  const [schedLoading, setSchedLoading] = useState(false);
  const [editSched, setEditSched] = useState(null); // id being edited
  const [editForm, setEditForm] = useState({ title: "", message: "", scheduled_at: "" });

  const schedBase = isAdmin ? "/admin/scheduled-announcements" : "/coach/scheduled-announcements";

  const loadScheduled = async () => {
    setSchedLoading(true);
    try {
      const { data } = await api.get(schedBase);
      setScheduled(data.items || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSchedLoading(false); }
  };

  const cancelScheduled = async (id) => {
    try {
      await api.delete(`${schedBase}/${id}`);
      toast.success("Scheduled Rally Call cancelled");
      loadScheduled();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const startEditSched = (s) => {
    setEditSched(s.id);
    setEditForm({ title: s.title, message: s.message, scheduled_at: toLocalInput(s.scheduled_at) });
  };

  const saveEditSched = async (s) => {
    if (!editForm.title.trim() || !editForm.message.trim()) { toast.error("Title and message are required"); return; }
    const iso = new Date(editForm.scheduled_at).toISOString();
    setActLoading(true);
    try {
      const payload = isAdmin
        ? { title: editForm.title, message: editForm.message, scheduled_at: iso,
            district_id: s.district_id || null, campus_id: s.campus_id || null, team_id: s.team_id || null, recipient_role: s.recipient_role || "team_member" }
        : { title: editForm.title, message: editForm.message, scheduled_at: iso, subgroup_ids: s.subgroup_ids || null };
      await api.put(`${schedBase}/${s.id}`, payload);
      toast.success("Scheduled Rally Call updated");
      setEditSched(null); loadScheduled();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActLoading(false); }
  };

  useEffect(() => {
    if (open && !isAdmin) {
      api.get("/coach/subgroups").then(({ data }) => { setSubs(data.items || []); setTeamTotal(data.team_total ?? null); }).catch(() => {});
      api.get("/coach/announcement-drafts?status=pending").then(({ data }) => setReqCount(data.pending_count || 0)).catch(() => {});
    }
  }, [open, isAdmin]);

  // Keep the pending-request count fresh even while the dialog is closed so the
  // trigger button can highlight when officers have drafts awaiting review.
  useEffect(() => {
    if (isAdmin) return;
    api.get("/coach/announcement-drafts?status=pending").then(({ data }) => setReqCount(data.pending_count || 0)).catch(() => {});
  }, [isAdmin]);

  const loadRequests = async () => {
    setReqLoading(true);
    try {
      const { data } = await api.get("/coach/announcement-drafts?status=pending");
      setReqs(data.items || []);
      setReqCount(data.pending_count || 0);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setReqLoading(false); }
  };

  const startReview = (d) => {
    setReviewing(d.id);
    setReviewForm({ title: d.title, message: d.message, subgroup_ids: [] });
  };

  const approveReview = async (id) => {
    if (!reviewForm.title.trim() || !reviewForm.message.trim()) { toast.error("Title and message are required"); return; }
    setActLoading(true);
    try {
      const { data } = await api.post(`/coach/announcement-drafts/${id}/approve`, {
        title: reviewForm.title, message: reviewForm.message,
        subgroup_ids: reviewForm.subgroup_ids.length ? reviewForm.subgroup_ids : null,
      });
      toast.success(data.message || "Approved & sent");
      setReviewing(null); loadRequests();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActLoading(false); }
  };

  const doReject = async (id) => {
    setActLoading(true);
    try {
      await api.post(`/coach/announcement-drafts/${id}/reject`, { reason: rejectReason });
      toast.success("Draft returned to the officer");
      setRejectId(null); setRejectReason(""); loadRequests();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActLoading(false); }
  };

  const toggleReviewSub = (id) => setReviewForm((f) => ({ ...f, subgroup_ids: f.subgroup_ids.includes(id) ? f.subgroup_ids.filter((x) => x !== id) : [...f.subgroup_ids, id] }));

  const campuses = ref.campuses.filter((c) => !form.district_id || c.district_id === form.district_id);
  const teams = ref.teams.filter((t) => !form.campus_id || t.campus_id === form.campus_id);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const url = isAdmin ? "/admin/announcements" : "/coach/announcements";
      const { data } = await api.get(url);
      setHistory(data.items || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setHistoryLoading(false); }
  };

  const switchTab = (next) => {
    setTab(next);
    if (next === "history") loadHistory();
    if (next === "requests") loadRequests();
    if (next === "scheduled") loadScheduled();
  };

  const submit = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Title and message are required"); return; }
    if (sendMode === "later") {
      const dt = new Date(scheduledAt);
      if (isNaN(dt.getTime())) { toast.error("Please pick a valid date and time"); return; }
      if (dt.getTime() <= Date.now()) { toast.error("Scheduled time must be in the future"); return; }
    }
    setLoading(true);
    try {
      if (sendMode === "later") {
        const iso = new Date(scheduledAt).toISOString();
        const payload = isAdmin
          ? { title: form.title, message: form.message, scheduled_at: iso, district_id: form.district_id || null, campus_id: form.campus_id || null, team_id: form.team_id || null, recipient_role: form.recipient_role }
          : { title: form.title, message: form.message, scheduled_at: iso, subgroup_ids: selSubs.length ? selSubs : null };
        await api.post(schedBase, payload);
        toast.success("Rally Call scheduled");
        setForm({ title: "", message: "", district_id: "", campus_id: "", team_id: "", recipient_role: "team_member" });
        setSelSubs([]); setSendMode("now"); setScheduledAt(nowLocalInput());
        switchTab("scheduled");
        return;
      }
      const url = isAdmin ? "/admin/broadcast" : "/coach/broadcast";
      const payload = isAdmin
        ? { title: form.title, message: form.message, district_id: form.district_id || null, campus_id: form.campus_id || null, team_id: form.team_id || null, recipient_role: form.recipient_role }
        : { title: form.title, message: form.message, subgroup_ids: selSubs.length ? selSubs : null, celebrate };
      const { data } = await api.post(url, payload);
      if (!isAdmin && celebrate) {
        const n = data.recipients ?? 0;
        toast.success(`🎉 Will celebrate ${n} student${n === 1 ? "" : "s"}!`);
      } else {
        toast.success(data.message || "Notice sent");
      }
      setForm({ title: "", message: "", district_id: "", campus_id: "", team_id: "", recipient_role: "team_member" });
      setSelSubs([]); setCelebrate(false);
      switchTab("history");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) switchTab(!isAdmin && reqCount > 0 ? "requests" : "compose"); }}>
      <DialogTrigger asChild>
        <Button
          className={`rounded-full ${!isAdmin && reqCount > 0 ? "bg-orange-500 hover:bg-orange-600" : "bg-slate-900 hover:bg-slate-800"}`}
          data-testid="broadcast-open-btn"
          data-pending={!isAdmin && reqCount > 0 ? "true" : "false"}
        >
          <Ear className="mr-2 h-4 w-4" /> Send a Rally Call
          {!isAdmin && reqCount > 0 && (
            <span className="ml-2 rounded-full bg-white/25 px-1.5 text-xs font-bold" data-testid="broadcast-open-badge">{reqCount}</span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="broadcast-dialog" className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Rally Call</DialogTitle>
          <DialogDescription className="sr-only">Compose, schedule, review officer drafts, or view the history of Rally Call announcements.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 rounded-full bg-slate-100 p-1" data-testid="broadcast-tabs">
          <button
            type="button"
            onClick={() => switchTab("compose")}
            data-testid="broadcast-tab-compose"
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${tab === "compose" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <PenLine className="h-3.5 w-3.5" /> Compose
          </button>
          <button
            type="button"
            onClick={() => switchTab("scheduled")}
            data-testid="broadcast-tab-scheduled"
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${tab === "scheduled" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <CalendarClock className="h-3.5 w-3.5" /> Scheduled
          </button>
          <button
            type="button"
            onClick={() => switchTab("history")}
            data-testid="broadcast-tab-history"
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${tab === "history" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <History className="h-3.5 w-3.5" /> History
          </button>
          {!isAdmin && (
            <button
              type="button"
              onClick={() => switchTab("requests")}
              data-testid="broadcast-tab-requests"
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${tab === "requests" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <Inbox className="h-3.5 w-3.5" /> Requests{reqCount > 0 ? <span className="ml-0.5 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white" data-testid="broadcast-requests-badge">{reqCount}</span> : null}
            </button>
          )}
        </div>

        {tab === "compose" ? (
          <>
            <div className="space-y-4">
              {isAdmin && (
                <>
                  <div className="space-y-2">
                    <Label>Send to</Label>
                    <div className="flex gap-2" data-testid="broadcast-recipient-selector">
                      <button type="button" onClick={() => setForm((f) => ({ ...f, recipient_role: "team_member" }))} data-testid="broadcast-recipient-members"
                        className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${form.recipient_role === "team_member" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Team members</button>
                      <button type="button" onClick={() => setForm((f) => ({ ...f, recipient_role: "coach" }))} data-testid="broadcast-recipient-coaches"
                        className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${form.recipient_role === "coach" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Coaches</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label className="text-xs">District</Label>
                    <Select value={form.district_id || ALL} onValueChange={(v) => setForm((f) => ({ ...f, district_id: v === ALL ? "" : v, campus_id: "", team_id: "" }))}>
                      <SelectTrigger data-testid="broadcast-district-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value={ALL}>All</SelectItem>{ref.districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label className="text-xs">Campus</Label>
                    <Select value={form.campus_id || ALL} onValueChange={(v) => setForm((f) => ({ ...f, campus_id: v === ALL ? "" : v, team_id: "" }))}>
                      <SelectTrigger data-testid="broadcast-campus-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value={ALL}>All</SelectItem>{campuses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label className="text-xs">Team</Label>
                    <Select value={form.team_id || ALL} onValueChange={(v) => setForm((f) => ({ ...f, team_id: v === ALL ? "" : v }))}>
                      <SelectTrigger data-testid="broadcast-team-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value={ALL}>All</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                </>
              )}
              <div className="space-y-2"><Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Practice moved to 4 PM" data-testid="broadcast-title-input" />
              </div>
              <div className="space-y-2"><Label>Message</Label>
                <Textarea rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="broadcast-message-input" />
              </div>
              {!isAdmin && (
                <div className="space-y-2">
                  <Label>Send to</Label>
                  <GroupMultiSelect subs={subs} value={selSubs} onChange={setSelSubs} allLabel={teamTotal != null ? `Entire team → ${teamTotal}` : "Entire team"} showCounts testId="broadcast-audience-select" />
                  {subs.length > 0 ? (
                    <p className="text-xs text-slate-400">{selSubs.length ? "Only members in the selected group(s) will receive this announcement." : "Reaches every active team member."}</p>
                  ) : (
                    <p className="text-xs text-slate-400" data-testid="broadcast-audience-empty-hint">This reaches the entire team. To send to specific groups (Freshmen, JV, Varsity, Officers…), first create them on the <Link to="/coach/members?tab=groups" className="font-semibold text-slate-600 underline underline-offset-2 hover:text-slate-900">Groups page</Link>.</p>
                  )}
                </div>
              )}
              {!isAdmin && (
                <button
                  type="button"
                  onClick={() => setCelebrate((v) => !v)}
                  data-testid="broadcast-celebrate-toggle"
                  aria-pressed={celebrate}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${celebrate ? "border-pink-300 bg-pink-50" : "border-slate-200 hover:bg-slate-50"}`}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${celebrate ? "bg-pink-100 text-pink-600" : "bg-slate-100 text-slate-500"}`}>
                    <PartyPopper className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-800">Celebration / Congrats</span>
                    <span className="block text-xs text-slate-500">Members see a confetti pop-up when they open this message.</span>
                  </span>
                  <span className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${celebrate ? "bg-pink-500" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${celebrate ? "translate-x-4" : "translate-x-0.5"}`} />
                  </span>
                </button>
              )}
              <div className="space-y-2 rounded-xl border border-slate-200 p-3" data-testid="broadcast-schedule-section">
                <div className="flex gap-2" data-testid="broadcast-send-mode">
                  <button type="button" onClick={() => setSendMode("now")} data-testid="broadcast-mode-now"
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors ${sendMode === "now" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    <Ear className="h-3.5 w-3.5" /> Send now
                  </button>
                  <button type="button" onClick={() => setSendMode("later")} data-testid="broadcast-mode-later"
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors ${sendMode === "later" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    <Clock className="h-3.5 w-3.5" /> Schedule for later
                  </button>
                </div>
                {sendMode === "later" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Date &amp; time</Label>
                    <Input type="datetime-local" value={scheduledAt} min={nowLocalInput()}
                      onChange={(e) => setScheduledAt(e.target.value)} data-testid="broadcast-schedule-input" />
                    <p className="text-xs text-slate-400">It will be posted automatically at the time you pick (your local time).</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400">
                {isAdmin
                  ? `Delivers an in-app notification to all active ${form.recipient_role === "coach" ? "coaches" : "team members"} matching the optional filters above (all if none selected).`
                  : "Delivers an in-app notification to all active, approved team members on your team."}
                {" "}It also sends via <strong>email, push, and text (SMS)</strong> — but only for the channels turned on in Settings (SMS reaches members who have a phone number saved).
              </p>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="broadcast-send-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {sendMode === "later" ? "Schedule Rally Call" : "Send a Rally Call"}
              </Button>
            </DialogFooter>
          </>
        ) : tab === "requests" ? (
          <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1" data-testid="broadcast-requests-list">
            {reqLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : reqs.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400" data-testid="broadcast-requests-empty">No pending announcement requests.</p>
            ) : (
              reqs.map((d) => (
                <div key={d.id} className="rounded-xl border border-slate-200 p-3" data-testid={`request-item-${d.id}`}>
                  {reviewing === d.id ? (
                    <div className="space-y-3">
                      <div className="space-y-1"><Label className="text-xs">Title</Label>
                        <Input value={reviewForm.title} onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))} data-testid={`review-title-${d.id}`} />
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Message</Label>
                        <Textarea rows={3} value={reviewForm.message} onChange={(e) => setReviewForm((f) => ({ ...f, message: e.target.value }))} data-testid={`review-message-${d.id}`} />
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Send to</Label>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => setReviewForm((f) => ({ ...f, subgroup_ids: [] }))}
                            className={`rounded-full px-3 py-1 text-xs font-medium ${reviewForm.subgroup_ids.length === 0 ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                            Entire team{teamTotal != null ? ` → ${teamTotal}` : ""}
                          </button>
                          {subs.map((s) => (
                            <button type="button" key={s.id} onClick={() => toggleReviewSub(s.id)}
                              className={`rounded-full px-3 py-1 text-xs font-medium ${reviewForm.subgroup_ids.includes(s.id) ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                              {s.name} → {s.active_count ?? s.member_count ?? 0}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approveReview(d.id)} disabled={actLoading} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid={`review-send-${d.id}`}>
                          {actLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Approve & send
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setReviewing(null)} className="rounded-full">Cancel</Button>
                      </div>
                    </div>
                  ) : rejectId === d.id ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-800">Return “{d.title}” to {d.author_name}?</p>
                      <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (optional)" data-testid={`reject-reason-${d.id}`} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => doReject(d.id)} disabled={actLoading} className="rounded-full bg-red-600 hover:bg-red-700" data-testid={`reject-confirm-${d.id}`}>
                          {actLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <X className="mr-1 h-3.5 w-3.5" />} Return draft
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setRejectId(null); setRejectReason(""); }} className="rounded-full">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-slate-900">{d.title}</p>
                        <span className="whitespace-nowrap text-xs text-slate-400">{formatSentAt(d.created_at)}</span>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{d.message}</p>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500">from {d.author_name}</span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setRejectId(d.id)} className="rounded-full text-red-600" data-testid={`request-reject-${d.id}`}>Reject</Button>
                          <Button size="sm" onClick={() => startReview(d)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid={`request-review-${d.id}`}>Review &amp; Send</Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        ) : tab === "scheduled" ? (
          <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1" data-testid="broadcast-scheduled-list">
            {schedLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : scheduled.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400" data-testid="broadcast-scheduled-empty">No Rally Calls scheduled. Use “Schedule for later” on the Compose tab.</p>
            ) : (
              scheduled.map((s) => (
                <div key={s.id} className="rounded-xl border border-slate-200 p-3" data-testid={`scheduled-item-${s.id}`}>
                  {editSched === s.id ? (
                    <div className="space-y-2">
                      <div className="space-y-1"><Label className="text-xs">Title</Label>
                        <Input value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} data-testid={`scheduled-edit-title-${s.id}`} />
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Message</Label>
                        <Textarea rows={3} value={editForm.message} onChange={(e) => setEditForm((f) => ({ ...f, message: e.target.value }))} data-testid={`scheduled-edit-message-${s.id}`} />
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Date &amp; time</Label>
                        <Input type="datetime-local" value={editForm.scheduled_at} min={nowLocalInput()} onChange={(e) => setEditForm((f) => ({ ...f, scheduled_at: e.target.value }))} data-testid={`scheduled-edit-when-${s.id}`} />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEditSched(s)} disabled={actLoading} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid={`scheduled-save-${s.id}`}>
                          {actLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditSched(null)} className="rounded-full">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-slate-900">{s.title}</p>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-slate-500"><CalendarClock className="h-3.5 w-3.5" /> {formatSentAt(s.scheduled_at)}</span>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{s.message}</p>
                      <div className="mt-2 flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => startEditSched(s)} className="rounded-full" data-testid={`scheduled-edit-${s.id}`}>
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => cancelScheduled(s.id)} className="rounded-full text-red-600" data-testid={`scheduled-cancel-${s.id}`}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1" data-testid="broadcast-history-list">
            {historyLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : history.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400" data-testid="broadcast-history-empty">No announcements sent yet.</p>
            ) : (
              history.map((a) => (
                <div key={a.id} className="rounded-xl border border-slate-200 p-3" data-testid="broadcast-history-item">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-slate-900">{a.title}</p>
                    <span className="whitespace-nowrap text-xs text-slate-400">{formatSentAt(a.created_at)}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{a.message}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {a.recipient_count} recipient{a.recipient_count === 1 ? "" : "s"}</span>
                    {a.audience && <span className="rounded-full bg-slate-100 px-2 py-0.5">{a.audience}</span>}
                    {isAdmin && a.sender_name && <span className="text-slate-400">by {a.sender_name}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BroadcastDialog;
